import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import StudentImageUpload from './StudentImageUpload';
import "./StudentDetails.css";
import configData from "../../configure"; // Import the configData object
import { sendResetEmail, checkFirebaseUserStatus, createUserInFirebase, deleteUserFromFirebaseBestEffort } from '../../firebaseUtils';

const StudentDetails = ({
  selectedUser,
  editedUser,
  isEditing,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  handleChange,
  father,
  mother,
  onSelectedUserChange = () => { },
  orgId,
}) => {
  const effectiveOrgId = orgId || selectedUser?.user?.orgId || selectedUser?.orgId || selectedUser?.studentInfo?.orgId;
  const isCustomOrg = effectiveOrgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
  const [studentImage, setStudentImage] = useState(null);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const baseUrl = "https://campus-user-documents.s3.ap-south-1.amazonaws.com/";
  const userId = selectedUser?.studentInfo?.userId;
  const { axios, token, userRole } = useAuth(); // Added userRole
  const [editedFather, setEditedFather] = useState(father);
  const [editedMother, setEditedMother] = useState(mother);
  const [isCreatingNewParents, setIsCreatingNewParents] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [resetEmailStatus, setResetEmailStatus] = useState(null);
  const [firebaseStatus, setFirebaseStatus] = useState(null);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  useEffect(() => {
    if (isLoginModalOpen) {
      const checkStatus = async () => {
        setFirebaseStatus('checking');
        const email = selectedUser?.user?.email || selectedUser?.user?.userName;
        if (email) {
          try {
            const status = await checkFirebaseUserStatus(email);
            setFirebaseStatus(status);
          } catch (e) {
            setFirebaseStatus(false);
          }
        } else {
          setFirebaseStatus(false);
        }
      };
      checkStatus();
    }
  }, [isLoginModalOpen, selectedUser]);

  const handleToggleFirebaseStatus = async () => {
    setIsTogglingStatus(true);
    const email = selectedUser?.user?.email || selectedUser?.user?.userName;
    try {
      if (firebaseStatus) {
        await deleteUserFromFirebaseBestEffort(email);
        setFirebaseStatus(false);
      } else {
        await createUserInFirebase(email, "egurukul@123456");
        setFirebaseStatus(true);
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setFirebaseStatus(true);
        alert('User already has a login account.');
      } else if (err.code === 'auth/wrong-password') {
        alert('Cannot disable login: User has changed their default password.');
      } else {
        alert(`Failed to toggle user login status: ${err.message || err.code || 'Unknown error'}`);
      }
    }
    setIsTogglingStatus(false);
  };

  const handleSendResetEmail = async () => {
    setResetEmailStatus('sending...');
    const email = selectedUser?.user?.email || selectedUser?.user?.userName;
    if (!email) {
      setResetEmailStatus('Error: No email address found');
      return;
    }
    const result = await sendResetEmail(email);
    if (result.success) {
      setResetEmailStatus('Success! Reset link sent to ' + email);
    } else {
      setResetEmailStatus('Error: ' + result.error);
    }
  };

  const formatBloodGroup = (value) => {
    if (!value) return "";

    const bloodGroupMap = {
      A_POSITIVE: "A+",
      A_NEGATIVE: "A-",
      B_POSITIVE: "B+",
      B_NEGATIVE: "B-",
      O_POSITIVE: "O+",
      O_NEGATIVE: "O-",
      AB_POSITIVE: "AB+",
      AB_NEGATIVE: "AB-",
    };

    return bloodGroupMap[value] || value;
  };

  const fetchPassportPhoto = async () => {
    if (!userId) return;
    try {
      const authHeader = token && token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const response = await axios.get(
        `${configData.CAMPUS_FILE_GET_BY_ENTITY}/${userId}?entityType=USER&generateDownloadUrls=true`,
        {
          headers: {
            Authorization: authHeader,
            accept: "application/json",
          },
        }
      );
      const docs = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
      const isPassportPhoto = (doc) => {
        const cat = (doc.fileCategory || doc.category || doc.documentType || doc.fileType || doc.type || '').toUpperCase();
        return cat === 'PASSPORT_PHOTO' || cat === 'PASSPORTPHOTO' || cat.includes('PASSPORT') || cat.includes('PHOTO') || cat.includes('PROFILE');
      };

      const passportPhoto = docs.find(isPassportPhoto) || docs[0];
      if (passportPhoto) {
        const photoUrl = passportPhoto.downloadUrl || passportPhoto.documentUrl || passportPhoto.fileUrl || passportPhoto.url || passportPhoto.s3Url || passportPhoto.path || null;
        if (photoUrl) {
          setStudentImage(photoUrl);
          setIsImageUploaded(true);
        }
      } else {
        setStudentImage("/default-profile.png");
        setIsImageUploaded(false);
      }
      setImageKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error("Error fetching passport photo:", error);
      setStudentImage("/default-profile.png");
      setIsImageUploaded(false);
    }
  };

  useEffect(() => {
    fetchPassportPhoto();
    setEditedFather(father); // Sync with props
    setEditedMother(mother); // Sync with props

    // Check if we need to create new parent records
    setIsCreatingNewParents(!father.userId && !mother.userId);

    console.log("Updated father prop:", father);
    console.log("Updated mother prop:", mother);
  }, [userId, father, mother]);

  const handleImageUpdate = async (data, isPermanent) => {
    if (isPermanent) {
      const finalUrl = data.downloadUrl || data.documentUrl;
      const updatedUser = {
        ...selectedUser,
        user: {
          ...selectedUser.user,
          documentsInfo: [
            ...(selectedUser.user.documentsInfo || []).filter(
              (doc) => doc.documentType !== "PASSPORT_PHOTO"
            ),
            {
              documentType: "PASSPORT_PHOTO",
              documentUrl: data.downloadUrl || data.documentUrl,
              creationTime: data.creationTime || new Date().toISOString(),
            },
          ],
        },
      };
      onSelectedUserChange(updatedUser);
      setStudentImage(finalUrl);
      setIsImageUploaded(true);
      await fetchPassportPhoto();
      setImageKey((prevKey) => prevKey + 1);
    } else {
      setStudentImage(data.tempUrl);
      setIsImageUploaded(false);
      setImageKey((prevKey) => prevKey + 1);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toISOString().split("T")[0];
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-GB");
  };

  const handleParentChange = (e, parentType) => {
    const { name, value } = e.target;
    if (parentType === 'father') {
      setEditedFather((prev) => ({ ...prev, [name]: value }));
    } else if (parentType === 'mother') {
      setEditedMother((prev) => ({ ...prev, [name]: value }));
    }
  };

  const createNewParent = async (parentData, parentType) => {
    try {
      // Skip if no name provided
      if (!parentData.name) return null;

      const currentTime = new Date().toISOString();

      const parentPayload = {
        user: {
          creationTime: currentTime,
          createdBy: "admin",
          lastUpdatedTime: currentTime,
          lastUpdatedBy: "admin",
          userId: null,
          orgId: selectedUser.user.orgId,
          userType: "PARENT",
          email: parentData.email || `${parentType.toLowerCase()}@example.com`,
          phoneNumber: parentData.phoneNumber || "",
          firstName: parentData.name,
          userStatus: "ACTIVE"
        },
        parentInfo: {
          parentType: parentType.toUpperCase(),
          occupation: parentData.occupation || "",
          listOfChildIds: [selectedUser.user.userId]
        }
      };

      console.log(`Creating new ${parentType} with payload:`, parentPayload);

      const response = await axios.post(
        configData.REGISTER_USER,
        parentPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      console.log(`${parentType} creation response:`, response.data);
      return response.data?.userId;
    } catch (error) {
      console.error(`Error creating ${parentType}:`, error);
      return null;
    }
  };

  const updateExistingParent = async (parentId, parentData, currentParent, parentType) => {
    const { data: existingParent } = await axios.get(
      `${configData.GET_USER_DETAILS}/${parentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      }
    );

    const currentTime = new Date().toISOString();
    const updatePayload = {
      user: {
        ...existingParent.user,
        userId: parentId,
        orgId: existingParent.user?.orgId || selectedUser.user.orgId,
        userType: "PARENT",
        firstName: parentData.name || currentParent.name || existingParent.user?.firstName || "",
        email: parentData.email || currentParent.email || existingParent.user?.email || `${parentType.toLowerCase()}@example.com`,
        phoneNumber: parentData.phoneNumber || currentParent.phoneNumber || existingParent.user?.phoneNumber || "",
        userStatus: existingParent.user?.userStatus || "ACTIVE",
        lastUpdatedTime: currentTime,
        lastUpdatedBy: "admin",
      },
      studentInfo: null,
      facultyInfo: null,
      staffInfo: null,
      parentInfo: {
        ...existingParent.parentInfo,
        parentType: parentType.toUpperCase(),
        occupation: parentData.occupation || currentParent.occupation || existingParent.parentInfo?.occupation || "",
        listOfChildIds: existingParent.parentInfo?.listOfChildIds || [selectedUser.user.userId],
      },
    };

    return axios.put(
      `${configData.UPDATE_USER}/${parentId}`,
      updatePayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
  };

  const handleSaveWithParents = async () => {
    try {
      setIsCreatingNewParents(true);

      // First save student details
      await handleSaveClick();

      let fatherId = father?.userId;
      let motherId = mother?.userId;
      let newParentIds = [];

      // Handle father details
      if (editedFather.name) {
        if (fatherId) {
          const fatherResponse = await updateExistingParent(fatherId, editedFather, father, "FATHER");
          console.log("Father update response:", fatherResponse.data);
        } else {
          const newFatherId = await createNewParent(editedFather, "FATHER");
          if (newFatherId) {
            fatherId = newFatherId;
            newParentIds.push(newFatherId);
          }
        }
      }

      // Handle mother details
      if (editedMother.name) {
        if (motherId) {
          const motherResponse = await updateExistingParent(motherId, editedMother, mother, "MOTHER");
          console.log("Mother update response:", motherResponse.data);
        } else {
          const newMotherId = await createNewParent(editedMother, "MOTHER");
          if (newMotherId) {
            motherId = newMotherId;
            newParentIds.push(newMotherId);
          }
        }
      }

      // If new parent IDs were created, update the student record
      if (newParentIds.length > 0) {
        // Get existing parent IDs from the student
        const existingParentIds = selectedUser?.studentInfo?.listOfParentIds || [];

        // Combine existing and new parent IDs
        const allParentIds = [...existingParentIds, ...newParentIds];

        // Fetch the existing user before update
        const existingUserResponse = await axios.get(
          `${configData.GET_USER_DETAILS}/${selectedUser.user.userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        const existingUser = existingUserResponse.data;

        // Update student with parent IDs
        const updatePayload = {
          ...existingUser,
          studentInfo: {
            ...existingUser.studentInfo,
            listOfParentIds: allParentIds,
          },
        };

        const updateResponse = await axios.put(
          `${configData.UPDATE_USER}/${selectedUser.user.userId}`,
          updatePayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Student update with parent IDs response:", updateResponse.data);
      }

      // Refresh the user data after all updates
      const updatedUserResponse = await axios.get(
        `${configData.GET_USER_DETAILS}/${selectedUser.user.userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      onSelectedUserChange(updatedUserResponse.data);
      setIsCreatingNewParents(false);
    } catch (error) {
      console.error("Error updating/creating parent details:", error);
      setIsCreatingNewParents(false);
    }
  };

  return (
    <div>
      <div className="StudentDetailsContainer">
        <div>

          <div className="studentdetailedit">
            <h2>Student Details</h2>

            {userRole === 'ADMIN' && (
              <div className="button-group">
                {isEditing ? (
                  <>
                    <button
                      className="Save-profile-custom-button"
                      onClick={handleSaveWithParents}
                    >
                      Save
                    </button>
                    <button
                      className="Cancel-profile-custom-button"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="discord-profile-custom-button"
                    onClick={handleEditClick}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="registration-form">
            <h3>Basic Information</h3>
            <div className="basicinfostudentdetail">


              <div className="form-row" style={{ justifyContent: 'center', marginBottom: '20px' }}>
                <div className="image-edit">
                  <img
                    key={imageKey}
                    src={studentImage || "/default-profile.png"}
                    alt="Student profile"
                    className="round-image-container"
                    onError={() => {
                      console.log("Image failed to load, falling back to default");
                      setStudentImage("/default-profile.png");
                    }}
                  />
                  <StudentImageUpload
                    selectedUser={selectedUser}
                    onImageUploaded={handleImageUpdate}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">First Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.firstName"
                      value={editedUser?.user?.firstName || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.firstName || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Middle Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.middleName"
                      value={editedUser?.user?.middleName || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.middleName || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Last Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.lastName"
                      value={editedUser?.user?.lastName || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.lastName || "N/A"}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Gender:</span>
                  {isEditing ? (
                    <select
                      name="user.gender"
                      value={editedUser?.user?.gender || ""}
                      onChange={handleChange}
                    >
                      <option value="MALE">MALE</option>
                      <option value="FEMALE">FEMALE</option>
                    </select>
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.gender || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Date of Birth:</span>
                  {isEditing ? (
                    <input
                      type="date"
                      name="user.dateOfBirth"
                      value={editedUser?.user?.dateOfBirth ? formatDateForInput(editedUser.user.dateOfBirth) : ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.dateOfBirth ? formatDateForDisplay(selectedUser.user.dateOfBirth) : "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group" style={{ background: 'transparent' }}></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Blood Group:</span>
                  {isEditing ? (
                    <select
                      name="user.bloodGroup"
                      value={editedUser?.user?.bloodGroup || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A_POSITIVE">A+</option>
                      <option value="A_NEGATIVE">A-</option>
                      <option value="B_POSITIVE">B+</option>
                      <option value="B_NEGATIVE">B-</option>
                      <option value="O_POSITIVE">O+</option>
                      <option value="O_NEGATIVE">O-</option>
                      <option value="AB_POSITIVE">AB+</option>
                      <option value="AB_NEGATIVE">AB-</option>
                    </select>
                  ) : (
                    <span className="InfoValue">
                      {formatBloodGroup(selectedUser?.user?.bloodGroup) || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Nationality:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.nationality"
                      value={editedUser?.user?.nationality || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.nationality || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Religion:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.religion"
                      value={editedUser?.user?.religion || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.religion || "N/A"}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Roll Number:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="studentInfo.rollNumber"
                      value={editedUser?.studentInfo?.rollNumber || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.rollNumber || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Admission Number:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="studentInfo.admissionId"
                      value={editedUser?.studentInfo?.admissionId || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.admissionId || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Admission Date:</span>
                  {isEditing ? (
                    <input
                      type="date"
                      name="studentInfo.dateOfJoining"
                      value={editedUser?.studentInfo?.dateOfJoining ? formatDateForInput(editedUser.studentInfo.dateOfJoining) : ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.dateOfJoining ? new Date(selectedUser.studentInfo.dateOfJoining).toLocaleDateString() : "N/A"}
                    </span>
                  )}
                </div>
                {isCustomOrg && (
                  <div className="form-group">
                    <span className="InfoLabel">Class Name:</span>
                    {isEditing ? (
                      <select
                        name="studentInfo.className"
                        value={editedUser?.studentInfo?.className || ""}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="">Select Class</option>
                        {["Nursery", "LKG", "UKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10"].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="InfoValue">
                        {selectedUser?.studentInfo?.className || selectedUser?.className || "N/A"}
                      </span>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className="registration-form">
            <h3>Contact Information</h3>

            <div className="contactinfostudentdetail">
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Address:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.addressLine1"
                      value={editedUser?.user?.addressDetails?.[0]?.addressLine1 || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.addressDetails?.[0]?.addressLine1 ||
                        "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Address Line 2:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.addressLine2"
                      value={editedUser?.user?.addressDetails?.[0]?.addressLine2 || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.addressDetails?.[0]?.addressLine2 ||
                        "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">City:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.city"
                      value={editedUser?.user?.addressDetails?.[0]?.city || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.addressDetails?.[0]?.city || "N/A"}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">State:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.state"
                      value={editedUser?.user?.addressDetails?.[0]?.state || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.addressDetails?.[0]?.state || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Pincode:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.pinCode"
                      value={editedUser?.user?.addressDetails?.[0]?.pinCode || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.addressDetails?.[0]?.pinCode || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Email:</span>
                  {isEditing ? (
                    <input
                      type="email"
                      name="user.email"
                      value={editedUser?.user?.email || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.email || "N/A"}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Phone Number:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.phoneNumber"
                      value={editedUser?.user?.phoneNumber || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.user?.phoneNumber || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Alternate Contact Number:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.addressDetails.0.alternatePhoneNumber"
                      value={editedUser?.user?.addressDetails?.[0]?.alternatePhoneNumber || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser.user.addressDetails?.[0]?.alternatePhoneNumber || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Aadhaar:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="user.lastFourDigitsOfAadhaarNumber"
                      value={editedUser?.user?.lastFourDigitsOfAadhaarNumber || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                        if (value.length <= 4) {
                          handleChange({ target: { name: e.target.name, value } });
                        }
                      }}
                      maxLength="4"
                      placeholder="XXXX"
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser.user.lastFourDigitsOfAadhaarNumber || "N/A"}
                    </span>
                  )}
                </div>

              </div>
            </div>
          </div>

          <div className="registration-form">
            <h3>Parent/Guardian Details</h3>

            <div className="parentinfostudentdetail">
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Father's Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={editedFather?.name || ""}
                      onChange={(e) => handleParentChange(e, 'father')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {father?.name || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Contact Number (Father):</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="phoneNumber"
                      value={editedFather?.phoneNumber || ""}
                      onChange={(e) => handleParentChange(e, 'father')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {father?.phoneNumber || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Father's Email:</span>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={editedFather?.email || ""}
                      onChange={(e) => handleParentChange(e, 'father')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {father?.email || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Father's Occupation:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="occupation"
                      value={editedFather?.occupation || ""}
                      onChange={(e) => handleParentChange(e, 'father')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {father?.occupation || "Not available"}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Mother's Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={editedMother?.name || ""}
                      onChange={(e) => handleParentChange(e, 'mother')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {mother?.name || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Contact Number (Mother):</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="phoneNumber"
                      value={editedMother?.phoneNumber || ""}
                      onChange={(e) => handleParentChange(e, 'mother')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {mother?.phoneNumber || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Mother's Email:</span>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={editedMother?.email || ""}
                      onChange={(e) => handleParentChange(e, 'mother')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {mother?.email || "Not available"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Mother's Occupation:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="occupation"
                      value={editedMother?.occupation || ""}
                      onChange={(e) => handleParentChange(e, 'mother')}
                    />
                  ) : (
                    <span className="InfoValue">
                      {mother?.occupation || "Not available"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <h3>Other Details</h3>

            <div className="otherstudentdetail">
              <div className="form-row">
                <div className="form-group">
                  <span className="InfoLabel">Previous School Details:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="studentInfo.previousSchoolDetails"
                      value={editedUser?.studentInfo?.previousSchoolDetails || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.previousSchoolDetails || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Scholarship Awards:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="studentInfo.scholarshipAwards"
                      value={editedUser?.studentInfo?.scholarshipAwards || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.scholarshipAwards || "N/A"}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <span className="InfoLabel">Transport Opted:</span>
                  {isEditing ? (
                    <select
                      name="studentInfo.transportOpted"
                      value={editedUser?.studentInfo?.transportOpted ? "Yes" : "No"}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "studentInfo.transportOpted",
                            value: e.target.value === "Yes", // Convert "Yes" to true, "No" to false
                          },
                        })
                      }
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  ) : (
                    <span className="InfoValue">
                      {selectedUser?.studentInfo?.transportOpted ? "Yes" : "No"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {userRole !== 'STUDENT' && (
              <>
                <h3>Login Details</h3>
                <div className="otherstudentdetail">
                  <div className="form-row">
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span className="InfoLabel">Username:</span>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span className="InfoValue">
                          {selectedUser?.user?.email || selectedUser?.user?.userName || "N/A"}
                        </span>
                        <button
                          onClick={() => setIsLoginModalOpen(true)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            color: '#0f172a',
                            fontWeight: '600',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          Manage Login
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                    </div>
                    <div className="form-group">
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoginModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
            width: '100%', maxWidth: '450px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginTop: 0, fontSize: '20px', color: '#0f172a', marginBottom: '16px' }}>Manage Login Details</h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <p style={{ margin: '0 12px 0 0', fontSize: '14px', color: '#475569' }}><strong>Login Access:</strong></p>
                {firebaseStatus === 'checking' ? (
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Checking Firebase...</span>
                ) : (
                  <button
                    onClick={handleToggleFirebaseStatus}
                    disabled={isTogglingStatus || firebaseStatus === null}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: firebaseStatus ? '#fef2f2' : '#f0fdf4',
                      border: `1px solid ${firebaseStatus ? '#fca5a5' : '#86efac'}`,
                      borderRadius: '6px',
                      color: firebaseStatus ? '#dc2626' : '#16a34a',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: isTogglingStatus ? 'not-allowed' : 'pointer',
                      opacity: isTogglingStatus ? 0.7 : 1
                    }}
                  >
                    {isTogglingStatus ? 'Processing...' : (firebaseStatus ? 'Disable Login' : 'Enable Login')}
                  </button>
                )}
              </div>
            </div>

            <div style={{
              backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '10px',
              border: '1px dashed #cbd5e1', marginBottom: '16px'
            }}>
              <h4 style={{ margin: '0 0 4px', color: '#0f172a', fontSize: '13px', fontWeight: '700' }}>Default Credentials</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#475569' }}>
                <strong>Password:</strong> egurukul@123456
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px',
              border: '1px solid #e2e8f0', marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: '15px' }}>Change/ Reset Password</h4>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#64748b' }}>
                For security reasons, passwords cannot be viewed directly. Send a password reset email to the user so they can securely create a new password.
              </p>
              <button
                onClick={handleSendResetEmail}
                disabled={resetEmailStatus === 'sending...'}
                style={{
                  width: '100%', padding: '10px', backgroundColor: '#0B4A8B', color: '#fff',
                  border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                  opacity: resetEmailStatus === 'sending...' ? 0.7 : 1
                }}
              >
                Click here Reset Password
              </button>
              {resetEmailStatus && (
                <p style={{
                  margin: '10px 0 0', fontSize: '13px', textAlign: 'center',
                  color: resetEmailStatus.startsWith('Error') ? '#dc2626' : '#16a34a',
                  fontWeight: '500'
                }}>
                  {resetEmailStatus}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setIsLoginModalOpen(false); setResetEmailStatus(null); }}
                style={{
                  padding: '8px 20px', backgroundColor: '#f1f5f9', border: 'none',
                  borderRadius: '8px', color: '#475569', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetails;
